import {TorasMenachemBrowser} from "./toras-menachem-browser.jsx";
// app.jsx — shell: nav, direction switcher, routing, reading controls, tweaks.
const { useState: useStateA, useEffect: useEffectA } = React;

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "direction": "beis",
  "lang": "both",
  "layout": "split",
  "readScale": 13,
  "readFont": "original",
  "fnStyle": "inline",
  "mobileFn": "popover",
  "topicApproach": "essay",
  "mapMode": "river",
  "mapEdges": "arc",
  "mapLabels": "hover",
  "accent": ""
}/*EDITMODE-END*/;

const DIRECTIONS = [
  // Two skies only: warm is the light mode, dark is true black.
  { id: "beis", label: "Warm Mode", short: "Warm", icon: "sun" },
  { id: "atlas", label: "Dark Mode", short: "Dark", icon: "moon" },
];
window.DIRECTIONS = DIRECTIONS;

function ReadControls({ lang, layout, scale, onLang, onLayout, onScale, mobile }) {
  return (
    <div className={"readctl" + (mobile ? " readctl-mobile" : "")}>
      {!mobile && <LayoutPicker value={layout} onChange={onLayout} />}
      <div className="dirswitch" style={{ padding: 2 }}>
        {[["en", "EN"], ["both", "Both"], ["he", "עב"]].map(([k, l]) => (
          <button key={k} className={"dir-btn" + (lang === k ? " on" : "")} style={{ padding: "5px 11px" }} onClick={() => onLang(k)}>{l}</button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button className="ghost-btn" style={{ padding: 6 }} onClick={() => onScale(Math.max(8, scale - 1))} aria-label="Smaller text"><span style={{ fontSize: 12, fontWeight: 800 }}>A</span></button>
        <button className="ghost-btn" style={{ padding: 6 }} onClick={() => onScale(Math.min(26, scale + 1))} aria-label="Larger text"><span style={{ fontSize: 17, fontWeight: 800 }}>A</span></button>
      </div>
    </div>
  );
}

function mayanosAccountInitial(account) {
  const value = account && account.authenticated && account.displayName ? account.displayName.trim() : "מ";
  return (Array.from(value)[0] || "מ").toLocaleUpperCase();
}

function AccountContent({ account, onOpenReader, onClose, compact = false }) {
  const [, setAccountTick] = useStateA(0);
  useEffectA(() => {
    const update = () => setAccountTick((value) => value + 1);
    ["mayanos-activity", "mayanos-bookmarks", "mayanos-settings"].forEach((event) => window.addEventListener(event, update));
    return () => ["mayanos-activity", "mayanos-bookmarks", "mayanos-settings"].forEach((event) => window.removeEventListener(event, update));
  }, []);
  const stats = (window.MAYANOS_ACCOUNT_SYNC && window.MAYANOS_ACCOUNT_SYNC.stats()) || account.stats || { learning: 0, completed: 0, bookmarks: 0, sheets: 0 };
  const recent = window.MAYANOS_ACTIVITY ? window.MAYANOS_ACTIVITY.read().slice(0, compact ? 2 : 3) : [];

  if (!account.ready) {
    return <div className="account-loading" role="status"><span className="account-spinner" /> Checking your account…</div>;
  }

  if (!account.authenticated) {
    return (
      <div className="account-signed-out">
        <div className="account-hero-mark"><Icon d="bookmark" size={22} /></div>
        <h2>Keep your learning with you</h2>
        <p>Sign in to save your progress, bookmarks, source sheets, and reader settings across devices.</p>
        <a className="btn btn-primary account-signin" href={account.signIn || "/signin-with-chatgpt"}>Sign in with ChatGPT</a>
        <span className="account-assurance">No separate Mayanos password. Your library remains public.</span>
        {(stats.learning > 0 || stats.bookmarks > 0 || stats.sheets > 0) && (
          <div className="account-local-note"><Icon d="check" size={14} /> Your current device data will be merged when you sign in.</div>
        )}
        <div className="account-section-label" style={{ marginTop: 16 }}>Privacy settings</div>
        <AnalyticsPreferenceToggle compact />
        <button className="privacy-footer-link" style={{ marginTop: 14 }} onClick={() => { window.MAYANOS_NAV && window.MAYANOS_NAV.go("privacy"); onClose(); }}>Privacy & analytics</button>
      </div>
    );
  }

  return (
    <div className="account-signed-in">
      <header className="account-user">
        <span className="account-user-avatar">{mayanosAccountInitial(account)}</span>
        <span><b>{account.displayName || "Mayanos learner"}</b><i>Signed in with ChatGPT</i></span>
        <span className={`account-sync-dot${account.syncing ? " syncing" : ""}`} />
      </header>
      <div className="account-sync-line">
        <Icon d={account.syncing ? "path" : "check"} size={14} />
        <span>{account.message || "Everything is saved"}</span>
      </div>
      <div className="account-stats" aria-label="Saved learning summary">
        <div><b>{stats.learning || 0}</b><span>Texts</span></div>
        <div><b>{stats.bookmarks || 0}</b><span>Bookmarks</span></div>
        <div><b>{stats.sheets || 0}</b><span>Sheets</span></div>
      </div>
      {recent.length > 0 && (
        <section className="account-recent">
          <div className="account-section-label">Continue learning</div>
          {recent.map((item) => (
            <button key={item.ref} onClick={() => { onOpenReader(item.ref); onClose(); }}>
              <ProgressRing value={Number(item.progress) || 0} size={31} />
              <span><b>{item.title || item.ref}</b><i>{Math.round((Number(item.progress) || 0) * 100)}% complete</i></span>
              <Icon d="chev" size={14} />
            </button>
          ))}
        </section>
      )}
      <div className="account-actions">
        <button className="btn" disabled={account.syncing} onClick={() => window.MAYANOS_ACCOUNT_SYNC && window.MAYANOS_ACCOUNT_SYNC.save()}>{account.syncing ? "Syncing…" : "Sync now"}</button>
        <a className="account-signout" href={account.signOut || "/signout-with-chatgpt"}>Sign out</a>
      </div>
      <div className="account-section-label" style={{ marginTop: 16 }}>Privacy settings</div>
      <AnalyticsPreferenceToggle compact />
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 12 }}>
        <button className="privacy-footer-link" onClick={() => { window.MAYANOS_NAV && window.MAYANOS_NAV.go("privacy"); onClose(); }}>Privacy & analytics</button>
        {account.analyticsAdmin && <button className="privacy-footer-link" onClick={() => { window.MAYANOS_NAV && window.MAYANOS_NAV.go("analytics"); onClose(); }}>Site analytics</button>}
      </div>
    </div>
  );
}

function AccountPopover({ open, account, onClose, onOpenReader }) {
  if (!open) return null;
  return (
    <>
      <button className="account-scrim" onClick={onClose} aria-label="Close account" />
      <aside className="account-popover" aria-label="Your Mayanos account">
        <div className="account-popover-head"><b>Your Mayanos</b><button className="pm-x" onClick={onClose} aria-label="Close"><Icon d="close" size={17} /></button></div>
        <AccountContent account={account} onOpenReader={onOpenReader} onClose={onClose} />
      </aside>
    </>
  );
}

function AccountPrompt({ account, prompt, onDismiss, onOpen }) {
  if (!prompt || !account.ready || account.authenticated) return null;
  const copy = prompt.reason === "bookmark"
    ? { title: "Keep this bookmark", body: "Sign in to keep your saved reading position on every device." }
    : prompt.reason === "workspace"
      ? { title: "Keep your research workspace", body: "Sign in to sync your source sheets, notes, and saved sources." }
      : { title: "Keep this source", body: "Sign in to keep saved sources and folders across devices." };
  return (
    <aside className="account-prompt" role="status" aria-live="polite">
      <span className="account-prompt-mark"><Icon d="bookmark" size={17} /></span>
      <span className="account-prompt-copy"><b>{copy.title}</b><small>{copy.body}</small></span>
      <button className="btn btn-primary account-prompt-action" onClick={onOpen}>Sign in</button>
      <button className="account-prompt-close" onClick={onDismiss} aria-label="Dismiss sign-in prompt"><Icon d="close" size={15} /></button>
    </aside>
  );
}

function MobileDrawer({ open, onClose, route, go, direction, onDirection, account, onOpenAccount }) {
  return (
    <>
      <div className={"drawer-scrim" + (open ? " on" : "")} onClick={onClose} />
      <aside className={"drawer" + (open ? " on" : "")} aria-hidden={!open}>
        <div className="drawer-head">
          <Brand />
          <button className="icon-btn" onClick={onClose} aria-label="Close menu"><Icon d="close" size={20} /></button>
        </div>
        <div className="drawer-sect drawer-account-label">Your Mayanos</div>
        <button className="drawer-account-summary" type="button" onClick={() => { onClose(); onOpenAccount(); }}>
          <span className={`drawer-account-avatar${account.authenticated ? " signed-in" : ""}`}>
            {account.authenticated ? mayanosAccountInitial(account) : <Icon d="user" size={18} />}
            <i />
          </span>
          <span className="drawer-account-copy">
            <b>{account.authenticated ? (account.displayName || "Your account") : "Sign in to Mayanos"}</b>
            <small>{account.authenticated ? "Progress and settings are synced" : "Save learning across your devices"}</small>
          </span>
          <Icon d="chev" size={16} />
        </button>
        <div className="drawer-sect">Navigate</div>
        <nav className="drawer-nav">
          {[["home", "Home", "compass"], ["reader", "Reader", "text"], ["library", "Library", "book"], ["explore", "Explore", "graph"], ["research", "Research", "layers"], ["dictionary", "Dictionary", "book"], ["topics", "Topics", "path"]].filter(([k]) => k !== "topics" || window.topicsEnabled()).map(([k, label, ic]) => (
            <button key={k} className={"drawer-link" + (route === k ? " on" : "")} onClick={() => go(k)}>
              <Icon d={ic} size={18} /> {label}
            </button>
          ))}
        </nav>
            <div className="drawer-sect">Display mode</div>
        <div className="drawer-dirs">
          {window.ThemeToggle && <window.ThemeToggle direction={direction} onDirection={onDirection} caption />}
        </div>
        <button className="privacy-footer-link" style={{ margin: "22px 18px" }} onClick={() => { go("privacy"); onClose(); }}>Privacy & analytics</button>
      </aside>
    </>
  );
}

// Shown the moment a text is chosen and kept until it is ready: the mark
// breathes, the page fills line by line, and the thread says we are still
// gathering the letter. Reduced-motion readers get the same layout at rest.
const LOADING_LINE_WIDTHS = [94, 84, 90, 70, 88, 78, 62];
function TextLoadingScreen({ status }) {
  const target = (status && status.target) || "";
  return (
    <main className="loading-screen" role="status" aria-live="polite">
      <div className="loading-stage">
        <div className="loading-mark" aria-hidden="true">
          <img src="/assets/mayanos-logo-192.png" width="34" height="34" alt="" />
        </div>
        <div className="loading-lines" aria-hidden="true">
          {LOADING_LINE_WIDTHS.map((width, index) => (
            <span key={index} style={{ width: `${width}%`, animationDelay: `${index * 110}ms` }} />
          ))}
        </div>
        <div className="loading-thread" aria-hidden="true"><span /></div>
      </div>
      <p className="loading-caption">
        <strong>{target ? `Opening ${target}` : "Opening the text"}</strong>
        <span>{(status && status.message) || "Gathering the sefer…"}</span>
      </p>
      {window.MAYANOS_SPONSORSHIP && (
        <div className="loading-sponsorship" role="note">
          <span>{window.MAYANOS_SPONSORSHIP.label}</span>
          <strong lang="he" dir="rtl" className="heb">{window.MAYANOS_SPONSORSHIP.compact}</strong>
        </div>
      )}
    </main>
  );
}

function ServiceState({ status, onNavigate }) {
  const state = status && status.state;
  const unavailable = state === "unavailable";
  const notFound = state === "not-found";
  const failed = unavailable || notFound;
  if (!failed) return <TextLoadingScreen status={status} />;
  const heading = notFound ? "We couldn't find that passage" : "The library is temporarily unavailable";
  return (
    <main className="service-state" role="alert" aria-live="polite">
      <div className="service-state-card">
        <div className="service-state-mark error">
          <Icon d="close" size={24} />
        </div>
        <h1>{heading}</h1>
        <p>{status && status.message ? status.message : "Connecting to the text service…"}</p>
        {unavailable && <button className="btn btn-primary" onClick={() => window.retryMayanos && window.retryMayanos()}>Try again</button>}
        {notFound && <button className="btn btn-primary" onClick={() => onNavigate && onNavigate("library")}>Browse the library</button>}
        {notFound && <button className="btn" onClick={() => onNavigate && onNavigate("reader")}>Open the reader</button>}
      </div>
    </main>
  );
}

function DictionaryPage() {
  return (
    <section className="dictionary-page">
      <div className="dictionary-page-intro">
        <span className="eyebrow">Mayanos tools</span>
        <h1>Dictionary</h1>
        <p>Look up Hebrew, Yiddish, and English terms while keeping a private history of the words you want to revisit.</p>
      </div>
      <DictionaryLookup />
    </section>
  );
}

function App() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  // Light mode is retired: a stored preference for it opens warm instead.
  const direction = t.direction === "chavrusa" ? "beis" : t.direction;
  const account = window.useMayanosAccount ? window.useMayanosAccount() : { ready: false, authenticated: false, message: "Checking account…" };
  const routeAtLocation = () => {
    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    const pathRoute = pathname === "/" || pathname === "/studio/index.html" ? "home" : pathname.split("/")[1];
    const known = ["home", "library", "explore", "research", "dictionary", "topics", "reader", "compare", "privacy", "analytics", "about", "contact", "methodology", "sources", "corrections", "search"];
    if (pathRoute === "r") return "reader";
    if (known.includes(pathRoute)) return pathRoute;
    const hashRoute = (location.hash || "").replace("#", "").trim().split("/")[0];
    return known.includes(hashRoute) ? hashRoute : "home";
  };
  const initialRoute = () => {
    return routeAtLocation();
  };
  const {topicsVisible} = window.useSiteSettings();
  const [route, setRoute] = useStateA(initialRoute);
  useEffectA(() => { if (route === "library") window.loadLibrary?.(); if (route === "home" && !window.WEEKLY_FEATURE) window.loadToday?.(); }, [route]);
  const [conceptId, setConceptId] = useStateA(() => new URLSearchParams(location.search).get("c") || "");
  const [drawer, setDrawer] = useStateA(false);
  const [accountOpen, setAccountOpen] = useStateA(false);
  const [accountPrompt, setAccountPrompt] = useStateA(null);
  const [paletteOpen, setPaletteOpen] = useStateA(false);
  const [shortcutsOpen, setShortcutsOpen] = useStateA(false);
  const [comparePair, setComparePair] = useStateA(null); // [idA, idB] | null
  const compareOriginRef = React.useRef(null);
  // live-data bridge: src/live.jsx swaps in real SICHA/SOURCES/COLLECTIONS from
  // the database (via the BFF) and calls window.__mayanosRerender to repaint.
  const [, setLiveTick] = useStateA(0);
  const [serviceStatus, setServiceStatus] = useStateA(() => window.MAYANOS_STATUS || { state: "loading", message: "Connecting to the Mayanos library…" });
  useEffectA(() => {
    window.__mayanosRerender = () => setLiveTick((n) => n + 1);
    const loadHashReader = () => {
      const m = location.pathname.match(/^\/reader\/(.+)$/) || (location.hash || "").match(/#reader\/(.+)$/);
      if (!m || !window.loadSicha) return false;
      let ref = m[1];
      let seg = "";
      const q = ref.indexOf("?");
      if (q >= 0) {
        const query = ref.slice(q + 1);
        ref = ref.slice(0, q);
        try { seg = new URLSearchParams(query).get("seg") || ""; } catch (e) {}
      }
      try { ref = decodeURIComponent(ref).replace(/_/g, " "); } catch (e) {}
      if (!seg) try { seg = new URLSearchParams(location.search).get("seg") || ""; } catch (e) {}
      try { seg = decodeURIComponent(seg).replace(/_/g, " "); } catch (e) {}
      const navigationState = history.state && typeof history.state === "object" ? history.state : {};
      window.loadSicha(ref, { syncUrl: false, recordActivity: false, origin: navigationState.mayanosOrigin || null, restore: navigationState.mayanosRestore || null }).then((loaded) => {
        if (!loaded) return;
        if (navigationState.mayanosRestore) {
          window.dispatchEvent(new CustomEvent("mayanos-restore-reader", { detail: navigationState.mayanosRestore }));
        } else if (seg) {
          window.__mayanosPendingSegmentRef = seg;
          window.dispatchEvent(new CustomEvent("mayanos-scroll-segment", { detail: { ref: seg } }));
        }
        setRoute("reader");
        setLiveTick((n) => n + 1);
      });
      return true;
    };
    const onStatus = (event) => setServiceStatus(event.detail || window.MAYANOS_STATUS);
    const onActivity = () => {
      if (window.MAYANOS_ACTIVITY) window.CONTINUE = window.MAYANOS_ACTIVITY.cards();
      setLiveTick((n) => n + 1);
    };
    window.addEventListener("mayanos-status", onStatus);
    window.addEventListener("mayanos-activity", onActivity);
    if (window.MAYANOS_STATUS) setServiceStatus(window.MAYANOS_STATUS);
    window.__mayanosLoadHashReader = loadHashReader;
    const onLive = () => loadHashReader();
    window.addEventListener("mayanos-live-ready", onLive);
    let tries = 0;
    const id = setInterval(() => {
      tries++;
      if (loadHashReader() || tries > 24) clearInterval(id);
    }, 250);
    return () => {
      clearInterval(id);
      window.removeEventListener("mayanos-live-ready", onLive);
      window.removeEventListener("mayanos-status", onStatus);
      window.removeEventListener("mayanos-activity", onActivity);
      if (window.__mayanosLoadHashReader === loadHashReader) delete window.__mayanosLoadHashReader;
    };
  }, []);
  useEffectA(() => {
    const onPop = () => {
      const next = routeAtLocation();
      if (next === "topics") {
        const concept = new URLSearchParams(location.search).get("c");
        if (concept) setConceptId(concept);
      }
      setRoute(next);
      if (next === "reader" && window.__mayanosLoadHashReader) window.__mayanosLoadHashReader();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const isMobile = window.useIsMobile();
  const openTopic = (id) => {
    if (!window.topicsEnabled()) return;
    if (id) setConceptId(id);
    if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("topic_opened", { topic: id || "" });
    setRoute("topics"); setDrawer(false);
  };
  const openCompare = (a, b) => {
    const origin = { route: routeAtLocation(), url: location.pathname + location.search, restore: window.__mayanosCaptureReaderLocation?.() || window.__mayanosReaderLocation || null };
    compareOriginRef.current = origin;
    const state = history.state && typeof history.state === "object" ? history.state : {};
    history.replaceState({ ...state, ...(origin.route === "reader" && origin.restore ? { mayanosRestore: origin.restore } : {}) }, "", origin.url);
    history.pushState({ mayanosCompareOrigin: origin }, "", "/compare");
    if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("page_view", { surface: "compare", left: a || "", right: b || "" });
    setComparePair([a || null, b || null]); setRoute("compare"); setDrawer(false);
  };
  // Open the reader, optionally loading a real text first (by ref or title).
  const openReaderRef = (ref, options) => {
    const target = ref || (window.SICHA && window.SICHA._liveRef) || window.__mayanosDefaultRef;
    if (target && window.loadSicha) {
      // Take the reader surface straight away: it shows the waiting screen
      // while the text is fetched, instead of leaving the previous page up
      // until the whole letter has arrived.
      setRoute("reader");
      setDrawer(false);
      return window.loadSicha(target, options || {});
    }
    setRoute("reader");
    setDrawer(false);
    return Promise.resolve(false);
  };
  const resumeReader = () => {
    setDrawer(false);
    setAccountOpen(false);
    const session = window.__mayanosReaderLocation || null;
    const recent = window.MAYANOS_ACTIVITY && window.MAYANOS_ACTIVITY.read ? window.MAYANOS_ACTIVITY.read()[0] : null;
    const target = (session && session.sectionRef) || (recent && recent.ref) || (window.SICHA && window.SICHA._liveRef) || window.__mayanosDefaultRef;
    const segmentRef = (session && session.segmentRef) || (recent && recent.lastSegmentRef) || "";
    const norm = (value) => String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
    const loadedRefs = [window.SICHA && window.SICHA._liveRef, window.SICHA && window.SICHA._liveTargetRef]
      .concat((window.SICHA && window.SICHA._loadedSections || []).flatMap((section) => [section.ref, section.targetRef]));
    const alreadyLoaded = !!target && loadedRefs.some((value) => norm(value) === norm(target));
    const restore = () => {
      if (alreadyLoaded && session) {
        window.dispatchEvent(new CustomEvent("mayanos-restore-reader", { detail: session }));
      } else if (segmentRef) {
        window.__mayanosPendingSegmentRef = segmentRef;
        window.dispatchEvent(new CustomEvent("mayanos-scroll-segment", { detail: { ref: segmentRef } }));
      }
    };
    if (alreadyLoaded || !target || !window.loadSicha) {
      setRoute("reader");
      setTimeout(restore, 140);
      return;
    }
    window.loadSicha(target, { recordActivity: false }).then((loaded) => {
      if (!loaded) return;
      setRoute("reader");
      setTimeout(restore, 180);
    });
  };
  const openExploreFromReader = (options = {}) => {
    const current = window.SICHA || {};
    const place = window.__mayanosReaderLocation || {};
    const origin = {
      url: location.pathname + location.search,
      ref: place.sectionRef || current._liveTargetRef || current._liveRef || current.id || "",
      sectionRef: place.sectionRef || current._liveTargetRef || current._liveRef || current.id || "",
      segmentRef: place.segmentRef || current._focusRef || "",
      scrollTop: Math.max(0, Number(place.scrollTop) || 0),
      title: place.title || current.titleEn || "the reader",
    };
    window.MAYANOS_EXPLORE_ORIGIN = origin;
    window.__mayanosExploreState = {
      origin,
      scope: "work",
      viewMode: options.viewMode || "river",
      selected: null,
      map: null,
      surprise: !!options.surprise,
    };
    if (location.pathname !== "/explore") history.pushState({ mayanosExploreOrigin: origin }, "", "/explore");
    setRoute("explore");
    setDrawer(false);
  };
  const backFromExplore = () => {
    if (history.state && history.state.mayanosExploreOrigin) {
      history.back();
      return;
    }
    resumeReader();
  };
  const go = (k) => {
    if (k === "topics" && !window.topicsEnabled()) return;
    if (k === "reader") resumeReader();
    else if (k === "explore" && route === "reader") openExploreFromReader();
    else { setRoute(k); setDrawer(false); }
  };
  // A multi-volume "set" (Igros Kodesh, Torah Menachem, …) drills into its volumes.
  const [drill, setDrill] = useStateA(null);
  const [tmSet, setTmSet] = useStateA(null);
  const [contentsWork, setContentsWork] = useStateA(null);
  useEffectA(() => { if (route !== "reader") window.cancelSourceNavigation?.("left_reader"); }, [route]);
  const openSefer = (work, parent = null) => {
    if (work?.beta || work?.sichosBeta) {
      const kind = work.beta ? "melukat" : "sichos";
      window.__mayanosLibraryCollection = kind;
      setRoute("library");
      window.dispatchEvent(new CustomEvent("mayanos-open-library-collection", { detail: kind }));
      return;
    }
    if (work?.children?.length && /^Toras? Menachem$/i.test(work.en || "")) { setTmSet(work); return; }
    if (work && work.children && work.children.length) setDrill({ ...work, parent });
    else if (work) {
      setDrill(null);
      const target = work.ref || work.en;
      if (work.readingSelection) openReaderRef(target, { alternateTexts: work.alternateTexts || [] });
      else setContentsWork({ ...work, parent });
    }
  };
  // command-palette navigation surface
  const nav = React.useMemo(() => ({
    go, openTopic, openReader: openReaderRef,
    openCompare, setDirection: (d) => setTweak("direction", d),
  }), []);
  useEffectA(() => {
    window.MAYANOS_NAV = nav;
    return () => { if (window.MAYANOS_NAV === nav) delete window.MAYANOS_NAV; };
  }, []);
  // ⌘K / Ctrl-K opens the palette anywhere
  useEffectA(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); setPaletteOpen((v) => !v); }
      else if (e.key === "/" && !/input|textarea/i.test((e.target.tagName || "")) && !e.target.isContentEditable) { e.preventDefault(); setPaletteOpen(true); }
      else if (e.key === "?" && !/input|textarea/i.test((e.target.tagName || "")) && !e.target.isContentEditable) { e.preventDefault(); setShortcutsOpen((v) => !v); }
      else if (e.key === "Escape") setShortcutsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.openPalette = (query = "") => { window.__mayanosPaletteQuery = query; setPaletteOpen(true); };
    window.openShortcuts = () => setShortcutsOpen(true);
    window.openComparePair = (sourceOrId) => {
      const source = sourceOrId && typeof sourceOrId === "object" ? sourceOrId : null;
      const activeSource = source ? {
        ...source,
        id: source.id || source.srcRef || source.ref || source.citation || source.title || "active-source",
      } : null;
      if (activeSource) window.ACTIVE_COMPARE_SOURCE = activeSource;
      const id = activeSource ? activeSource.id : sourceOrId;
      const firstSource = (window.SOURCES || []).find((s) => s.id);
      const firstAvailableSource = (firstSource && firstSource.id) || "sicha";
      const right = id && id !== "sicha" ? "sicha" : firstAvailableSource;
      openCompare(id || "sicha", right);
    };
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // route transition: re-animate the content region whenever the route changes
  const routeRef = React.useRef(null);
  React.useLayoutEffect(() => {
    if (window.entrance) window.entrance(routeRef.current, { y: 0, dur: 420, easing: window.EASE_SOFT });
  }, [route]);
  // measure nav height into --nav-h so full-height screens (reader) fit exactly
  React.useLayoutEffect(() => {
    const setNav = () => {
      const nav = document.querySelector(".topnav");
      if (nav) document.documentElement.style.setProperty("--nav-h", Math.round(nav.getBoundingClientRect().height) + "px");
    };
    setNav();
    window.addEventListener("resize", setNav);
    return () => window.removeEventListener("resize", setNav);
  }, [route, isMobile]);

  // apply direction to <html>
  useEffectA(() => { document.documentElement.dataset.direction = direction; }, [direction]);
  // deep-linkable initial route via #hash (used by the mobile-preview harness)
  useEffectA(() => {
    // Preserve old hash links while canonical navigation uses clean paths.
    const apply = () => { const base = (location.hash || "").replace("#", "").trim().split("/")[0]; if (base) setRoute(base); if (base === "reader" && window.__mayanosLoadHashReader) window.__mayanosLoadHashReader(); };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);
  useEffectA(() => {
    if (["reader", "search", "corrections"].includes(route)) return;
    const sharedSheet = route === "research" ? new URLSearchParams(location.search).get("sheet") : "";
    const next = route === "home" ? "/"
      : route === "topics" ? `/topics?c=${encodeURIComponent(conceptId)}`
        : route === "research" && sharedSheet ? `/research?sheet=${encodeURIComponent(sharedSheet)}`
          : `/${route}`;
    const current = location.pathname + location.search;
    if (current !== next) {
      const method = location.pathname.startsWith("/studio/") ? "replaceState" : "pushState";
      history[method](null, "", next);
    }
  }, [route, conceptId]);
  useEffectA(() => {
    const labels = { about: "About", contact: "Contact", methodology: "Methodology & accuracy", sources: "Sources & licensing", corrections: "Text corrections", search: "Search", home: "A Living Library of Chassidus", library: "Library", explore: "Source Map", research: "Research Workspace", dictionary: "Dictionary", topics: "Concepts", compare: "Compare Texts", privacy: "Privacy & Analytics", analytics: "Site Analytics" };
    const pageTitle = route === "reader" && window.SICHA && window.SICHA.titleEn
      ? window.SICHA.titleEn
      : labels[route] || "A Living Library of Chassidus";
    document.title = `${pageTitle} — Mayanos`;
    const canonical = `${location.origin}${location.pathname}`;
    const canonicalLink = document.getElementById("mayanos-canonical");
    if (canonicalLink) canonicalLink.setAttribute("href", canonical);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", canonical);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", document.title);
    const reader = route === "reader" && window.SICHA ? window.SICHA : null;
    const description = reader
      ? `${reader.titleEn || "Read this Chassidus text"}${reader.collection ? ` · ${reader.collection}` : ""}. Explore its sources and bilingual translation in Mayanos.`
      : "Learn, search, research, and map Chassidus through bilingual texts and their sources.";
    const setMeta = (selector, value) => { const node = document.querySelector(selector); if (node) node.setAttribute("content", value); };
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[name="twitter:title"]', document.title);
    setMeta('meta[name="twitter:description"]', description);
    let ld = document.getElementById("mayanos-structured-data");
    if (!ld) { ld = document.createElement("script"); ld.id = "mayanos-structured-data"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": reader ? "Article" : "WebSite",
      name: document.title.replace(/ — Mayanos$/, ""),
      description,
      url: canonical,
      isPartOf: { "@type": "WebSite", name: "Mayanos", url: location.origin + "/" },
    });
  }, [route, conceptId, window.SICHA && window.SICHA._liveRef]);
  useEffectA(() => {
    const onPrompt = (event) => {
      const reason = event && event.detail && event.detail.reason || "source";
      if (!account.ready || account.authenticated) return;
      let seen = 0;
      try { seen = Number(localStorage.getItem(`mayanos-account-prompt:${reason}`) || 0); } catch (e) {}
      if (seen && Date.now() - seen < 7 * 24 * 60 * 60 * 1000) return;
      try { localStorage.setItem(`mayanos-account-prompt:${reason}`, String(Date.now())); } catch (e) {}
      setAccountPrompt({ reason });
      if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("account_prompt_shown", { reason });
    };
    window.addEventListener("mayanos-account-prompt", onPrompt);
    return () => window.removeEventListener("mayanos-account-prompt", onPrompt);
  }, [account.ready, account.authenticated]);
  useEffectA(() => {
    if (serviceStatus.state !== "ready" || !window.MAYANOS_ANALYTICS) return;
    window.MAYANOS_ANALYTICS.track("page_view", {
      page: route,
      concept: route === "topics" ? conceptId : "",
    });
    if (route === "research") window.MAYANOS_ANALYTICS.track("workspace_opened", { page: route });
  }, [route, conceptId, serviceStatus.state]);
  // expose whether the bottom tab bar is showing, so CSS can reserve space
  useEffectA(() => {
    document.documentElement.dataset.tabbar = (isMobile && route !== "reader") ? "1" : "0";
  }, [isMobile, route]);
  // optional accent override (tweak)
  useEffectA(() => {
    const el = document.documentElement;
    if (t.accent) el.style.setProperty("--accent", t.accent); else el.style.removeProperty("--accent");
  }, [t.accent]);

  const NAV = [["home", "Home"], ["reader", "Reader"], ["library", "Library"], ["explore", "Explore"], ["research", "Research"], ["dictionary", "Dictionary"], ["topics", "Topics"]];

  return (
    <div id="app">
      <nav className={"topnav" + (isMobile ? " topnav-mobile" : "")}>
        <div className="nav-brand-button" onClick={() => go("home")} style={{ cursor: "pointer", display: "flex" }}><Brand /></div>
        {!isMobile && (
          <>
            <div className="nav-links">
              {NAV.filter(([k]) => k !== "topics" || topicsVisible).map(([k, label]) => (
                <button key={k} className={"nav-link" + (route === k ? " on" : "")}
                  onClick={() => go(k)}>{label}</button>
              ))}
            </div>
            <div className="nav-spacer" />
            {/* reading controls live inside the reader toolbar now (cleaner nav) */}
            {window.ThemeToggle && <window.ThemeToggle direction={direction} onDirection={(v) => setTweak("direction", v)} />}
            {route !== "reader" && route !== "home" && (
              <div className="nav-cmd" role="button" aria-label="Search or jump to" tabIndex="0" onClick={() => setPaletteOpen(true)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setPaletteOpen(true); }}>
                <Icon d="search" size={16} /><span className="nav-cmd-spacer">Search or jump to…</span><span className="kbd">⌘K</span>
              </div>
            )}
            <button className={`nav-avatar${account.authenticated ? " signed-in" : ""}`} onClick={() => setAccountOpen((value) => !value)} aria-label={account.authenticated ? `Account: ${account.displayName || "signed in"}` : "Sign in or open account"}>
              {mayanosAccountInitial(account)}
              <span className="nav-account-dot" />
            </button>
          </>
        )}
        {isMobile && (
          <>
            <div className="nav-spacer" />
            {window.ThemeToggle && <window.ThemeToggle direction={direction} onDirection={(v) => setTweak("direction", v)} />}
            {route !== "home" && <button className="icon-btn" onClick={() => setPaletteOpen(true)} aria-label="Search"><Icon d="search" size={20} /></button>}
            {account.authenticated ? (
              <button className="nav-avatar signed-in" onClick={() => setAccountOpen(true)} aria-label={`Account: ${account.displayName || "signed in"}`}>
                {mayanosAccountInitial(account)}
                <span className="nav-account-dot" />
              </button>
            ) : (
              <button className="mobile-signin-btn" onClick={() => setAccountOpen(true)} aria-label="Sign in to Mayanos">
                <Icon d="user" size={17} /> <span>Sign in</span>
              </button>
            )}
            <button className="icon-btn nav-menu-btn" onClick={() => setDrawer(true)} aria-label="Open menu"><Icon d="menu" size={20} /></button>
          </>
        )}
      </nav>

      {isMobile && (
        <MobileDrawer open={drawer} onClose={() => setDrawer(false)} route={route} go={go}
          direction={direction} onDirection={(v) => { setTweak("direction", v); }} account={account}
          onOpenAccount={() => setAccountOpen(true)} />
      )}

      <a className="skip-link" href="#main-content">Skip to content</a>
      <AccountPopover open={accountOpen} account={account} onClose={() => setAccountOpen(false)} onOpenReader={openReaderRef} />
      <AccountPrompt account={account} prompt={accountPrompt}
        onDismiss={() => { if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("account_prompt_dismissed", { reason: accountPrompt && accountPrompt.reason || "source" }); setAccountPrompt(null); }}
        onOpen={() => { if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("account_prompt_clicked", { reason: accountPrompt && accountPrompt.reason || "source" }); setAccountPrompt(null); setAccountOpen(true); }} />

      {serviceStatus.state !== "ready" && !["privacy", "analytics", "about", "contact", "methodology", "sources", "corrections", "search", "library"].includes(route) ? <ServiceState status={serviceStatus} onNavigate={go} /> : <main id="main-content" tabIndex={-1} className="route-fade" ref={routeRef} key={route}>
      {["about", "contact", "methodology", "sources", "corrections"].includes(route) && <InfoPage page={route} />}
      {route === "search" && <SearchPage onOpen={openReaderRef} />}
      {route === "home" && (isMobile
        ? <MobileHome direction={direction} onDirection={(v) => setTweak("direction", v)} onOpenSicha={(ref) => openReaderRef(ref)} onOpenTopic={openTopic} onOpenExplore={() => setRoute("explore")} onOpenResearch={() => setRoute("research")} onOpenLibrary={() => setRoute("library")} />
        : <Home direction={direction} onDirection={(v) => setTweak("direction", v)} onOpenSicha={() => openReaderRef()} onOpenRef={openReaderRef} onOpenTopic={openTopic} onOpenExplore={() => setRoute("explore")} onOpenResearch={() => setRoute("research")} onOpenLibrary={() => setRoute("library")} />)}
      {route === "library" && <Library onOpenSefer={openSefer} />}
      {(route === "reader") && (
        <Reader lang={t.lang} layout={t.layout} readScale={t.readScale} readFont={t.readFont} onFont={(v) => setTweak("readFont", v)} fnStyle={t.fnStyle}
          isMobile={isMobile} onLang={(v) => { setTweak("lang", v); window.MAYANOS_ANALYTICS && window.MAYANOS_ANALYTICS.track("reader_setting_changed", { setting: "language", value: v }); }} onLayout={(v) => { setTweak("layout", v); window.MAYANOS_ANALYTICS && window.MAYANOS_ANALYTICS.track("reader_setting_changed", { setting: "layout", value: v }); }} onScale={(v) => { setTweak("readScale", v); window.MAYANOS_ANALYTICS && window.MAYANOS_ANALYTICS.track("reader_setting_changed", { setting: "text_size", value: v }); }}
          fnMode={t.mobileFn} onFnStyle={(v) => { setTweak("fnStyle", v); window.MAYANOS_ANALYTICS && window.MAYANOS_ANALYTICS.track("reader_setting_changed", { setting: "footnote_style", value: v }); }} onFnMode={(v) => { setTweak("mobileFn", v); window.MAYANOS_ANALYTICS && window.MAYANOS_ANALYTICS.track("reader_setting_changed", { setting: "mobile_footnotes", value: v }); }}
          onResetDisplay={() => setTweak({ lang: "both", layout: "split", readScale: 13, readFont: "original", fnStyle: "inline", mobileFn: "popover" })}
          direction={direction} onDirection={(v) => setTweak("direction", v)}
          onBack={() => setRoute("library")} onOpenTopic={openTopic} onOpenResearch={() => setRoute("research")} onOpenExplore={openExploreFromReader} />
      )}
      {route === "explore" && (
        <ExplorePage t={t} setTweak={setTweak} onOpenReader={() => setRoute("reader")} onBackReader={backFromExplore} />
      )}
      {route === "topics" && topicsVisible && (
        <TopicPage approach={t.topicApproach} conceptId={conceptId}
          onApproach={(v) => setTweak("topicApproach", v)} onConcept={setConceptId}
          onOpenReader={(ref) => openReaderRef(ref)} />
      )}
      {route === "topics" && !topicsVisible && <section className="hsection"><h1>This page is unavailable</h1><button className="btn" onClick={() => go("library")}>Open the library</button></section>}
      {route === "research" && <Research onOpenReader={openReaderRef} onOpenCompare={openCompare} />}
      {route === "dictionary" && <DictionaryPage />}
      {route === "compare" && <ComparePage pair={comparePair} onChangePair={setComparePair} onBack={() => {
        const origin = history.state?.mayanosCompareOrigin || compareOriginRef.current;
        if (origin?.route === "reader" && origin.restore) window.__mayanosPendingReaderRestore = origin.restore;
        if (history.state?.mayanosCompareOrigin) history.back();
        else setRoute(origin?.route || "reader");
      }} onOpenReader={() => setRoute("reader")} />}
      {route === "privacy" && <PrivacyPage />}
      {route === "analytics" && <AnalyticsAdminPage />}
      </main>}
      {route !== "reader" && <SiteFooter />}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} nav={nav} />

      {shortcutsOpen && (
        <div className="toc-scrim" onClick={() => setShortcutsOpen(false)}>
          <section className="toc-panel shortcuts-sheet" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts" onClick={(event) => event.stopPropagation()}>
            <div className="toc-head">
              <div><b>Keyboard shortcuts</b><span className="toc-count">Reading and search</span></div>
              <button className="icon-btn" onClick={() => setShortcutsOpen(false)} aria-label="Close"><Icon d="close" size={18} /></button>
            </div>
            <ul className="shortcuts-list">
              <li><span className="keys"><kbd>⌘</kbd><kbd>K</kbd></span><span>Search the library</span></li>
              <li><span className="keys"><kbd>/</kbd></span><span>Search the library</span></li>
              <li><span className="keys"><kbd>?</kbd></span><span>Show this list</span></li>
              <li><span className="keys"><kbd>j</kbd><kbd>k</kbd></span><span>Next / previous section while reading</span></li>
              <li><span className="keys"><kbd>↑</kbd><kbd>↓</kbd></span><span>Move through the sefer contents</span></li>
              <li><span className="keys"><kbd>Enter</kbd></span><span>Open the highlighted section</span></li>
              <li><span className="keys"><kbd>Esc</kbd></span><span>Close a panel</span></li>
            </ul>
            <p className="shortcuts-note">The reader header keeps contents · previous · next · text settings in reach; print is beside text settings; bookmark, share and find live under ⋯.</p>
          </section>
        </div>
      )}

      {contentsWork && <LibraryContentsBrowser work={contentsWork} onClose={() => setContentsWork(null)}
        onBack={contentsWork.parent ? () => { setDrill(contentsWork.parent); setContentsWork(null); } : null}
        onOpen={ref => { const work = contentsWork; setContentsWork(null); openReaderRef(ref, { alternateTexts: work.alternateTexts || [] }); }} />}
      {tmSet && <TorasMenachemBrowser onClose={() => setTmSet(null)} onOpen={ref => { setTmSet(null); openReaderRef(ref); }} onVolumes={() => { setDrill(tmSet); setTmSet(null); }} />}
      {/* drill into a multi-volume set (Igros Kodesh, Torah Menachem, …) */}
      {drill && (
        <div className="toc-scrim" onClick={() => setDrill(null)}>
          <div className="toc-panel volume-picker" role="dialog" aria-modal="true" aria-label={drill.en} onClick={(e) => e.stopPropagation()}>
            <div className="toc-head">
              {drill.parent && <button className="ghost-btn" onClick={() => setDrill(drill.parent)}>Back to volumes</button>}
              <div><span lang="he" dir="rtl" className="heb" style={{ fontSize: 20, marginInlineEnd: 8 }}>{drill.he}</span><b>{drill.en}</b><span className="toc-count">{drill.countLabel || `${drill.count} volumes`}</span></div>
              <button className="icon-btn" onClick={() => setDrill(null)} aria-label="Close"><Icon d="close" size={18} /></button>
            </div>
            <div className="drill-grid">
              {drill.children.map((c) => (
                <button key={c.ref} className="drill-item" onClick={() => openSefer(c, drill)}>
                  <span lang="he" dir="rtl" className="heb">{c.he}</span>
                  <span className="drill-en">{c.en}</span>
                  {c.editionLabel && <small>{c.editionLabel}</small>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {isMobile && route !== "reader" && <MobileTabBar route={route} go={go} />}

      <TweaksPanel>
        <TweakSection label="Display mode" />
        <TweakRadio label="Mode" value={direction}
          options={DIRECTIONS.map((d) => ({ value: d.id, label: d.label }))}
          onChange={(v) => setTweak("direction", v)} />
        <TweakSection label="Reading" />
        <TweakRow label="Layout">
          <LayoutPicker value={t.layout} onChange={(v) => setTweak("layout", v)} />
        </TweakRow>
        <TweakRadio label="Language" value={t.lang} options={["he", "both", "en"]} onChange={(v) => setTweak("lang", v)} />
        <TweakSlider label="Text size" value={t.readScale} min={8} max={26} unit="px" onChange={(v) => setTweak("readScale", v)} />
        <TweakRadio label="Footnotes" value={t.fnStyle} options={["inline", "margin"]} onChange={(v) => setTweak("fnStyle", v)} />
        <TweakRadio label="Footnotes (mobile)" value={t.mobileFn} options={["sheet", "popover", "inline"]} onChange={(v) => setTweak("mobileFn", v)} />
        <TweakSection label="Concept page" />
        <TweakRadio label="Approach" value={t.topicApproach} options={["essay", "atlas"]} onChange={(v) => setTweak("topicApproach", v)} />
        <TweakSection label="Source map" />
        <TweakRadio label="Default view" value={t.mapMode} options={["radial", "river"]} onChange={(v) => setTweak("mapMode", v)} />
        <TweakRadio label="Edges" value={t.mapEdges} options={["arc", "line"]} onChange={(v) => setTweak("mapEdges", v)} />
        <TweakRadio label="Labels" value={t.mapLabels} options={["always", "hover"]} onChange={(v) => setTweak("mapLabels", v)} />
        <TweakSection label="Accent override" />
        <TweakColor label="Accent" value={t.accent || "#1c6b66"}
          options={["#1c6b66", "#2563d6", "#6d4ff0", "#a9762f", "#38c6b4"]}
          onChange={(v) => setTweak("accent", v)} />
      </TweaksPanel>
    </div>
  );
}

class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) { console.error("Mayanos interface error", error); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="service-state" role="alert">
        <div className="service-state-card">
          <div className="service-state-mark error"><Icon d="close" size={24} /></div>
          <h1>This view could not be opened</h1>
          <p>Your saved reading activity and source sheets are still in this browser.</p>
          <button className="btn btn-primary" onClick={() => location.reload()}>Reload Mayanos</button>
        </div>
      </main>
    );
  }
}

ReactDOM.createRoot(document.getElementById("root")).render(<AppErrorBoundary><App /></AppErrorBoundary>);
